import { ActionButton, FormInputRow, Input } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Divider, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { kebabCase } from 'lodash'
import React, { useState } from 'react'
import Metering from '../components/metering'
import StubbedContent from '../components/stubbed-content'
import TempUserField from '../components/tempuser-field'
import UsernameLogin from '../components/username-login'
import { useTenantFormContext } from '../tenant-form-context'
import { useStyles } from './style'
import { buildToolkitUrl } from '../../../helpers/toolkit'
import { useArkMode } from '../../../hooks/useArkMode'
import { Controller } from 'react-hook-form'
import { AWS_REGIONS, TOOLKIT_TYPE } from '../../../util/constants'

const isLocalEnv = process.env.REACT_APP_ENVIRONMENT === 'local'

const toolkitTypeOptions = [
  { label: 'Club', value: TOOLKIT_TYPE.club },
  { label: 'Enterprise Toolkit', value: TOOLKIT_TYPE.enterprise },
  { label: 'Small Business Toolkit', value: TOOLKIT_TYPE.smallBusinessToolkit }
]

const toolkitRegionOptions = [
  { label: 'Europe (London) ', value: AWS_REGIONS.london },
  { label: 'US West (N. California)', value: AWS_REGIONS.california },
  { label: 'Asia Pacific (Singapore) ', value: AWS_REGIONS.singapore },
  { label: 'Asia Pacific (Sydney)', value: AWS_REGIONS.sydney },
  { label: 'South America (São Paulo)', value: AWS_REGIONS.saoPaulo }
]

if (isLocalEnv) {
  toolkitRegionOptions.push({ label: 'Europe (Ireland)', value: AWS_REGIONS.ireland })
}

const DetailTab = () => {
  const classes = useStyles()
  const {
    form,
    envInfo,
    isEdit,
    onUpdateTenant,
    tenant,
    handleNextClick,
    mainClasses
  } = useTenantFormContext()
  const {
    watch,
    setValue,
    formState: { errors },
    getValues,
    setError,
    clearErrors,
    trigger
  } = form
  const [loading, setLoading] = useState(false)
  const { isAdminMode } = useArkMode()
  const tenantName = watch('name')
  const toolkitType = watch('toolkitType')
  const isSBT = toolkitType === TOOLKIT_TYPE.smallBusinessToolkit
  const isClub = toolkitType === TOOLKIT_TYPE.club
  const isEnterprise = toolkitType === TOOLKIT_TYPE.enterprise
  const featureReleaseDate = new Date('2025-03-27T00:00:00.000Z')
  const tenantCreatedAt = tenant?.createdAt ? new Date(tenant.createdAt) : null
  const showUsernameLogin = (isEnterprise && !tenantCreatedAt) || (isEnterprise && tenantCreatedAt && tenantCreatedAt > featureReleaseDate)
  const defaultRegion = isLocalEnv ? process.env.REACT_APP_API_GATEWAY_REGION : AWS_REGIONS.london

  React.useEffect(() => {
    if (!tenantName && !toolkitType && !isEdit) {
      setValue('tenantKey', '')
      setValue('tenantUrl', '')
      setValue('toolkitType', TOOLKIT_TYPE.enterprise)
      setValue('region', defaultRegion)
      return
    }

    const tenantKey = tenant?.tenantKey || kebabCase(tenantName)
    if (!isEdit) setValue('tenantKey', tenantKey)

    if (!envInfo) {
      setValue('tenantUrl', '')
      return
    }

    const { stage, tld } = envInfo
    const toolkitUrl = buildToolkitUrl(tenantKey, stage, tld)

    setValue('tenantUrl', toolkitUrl)
  }, [tenantName, tenant, setValue, envInfo, isEdit, toolkitType, defaultRegion])

  const [isNameInUse] = useEvent([
    {
      eventName: 'tenantGet',
      onSuccess: e => {
        if (!e) {
          clearErrors('tenantKey')
        } else {
          setError('tenantKey', {
            type: 'exist',
            message: 'Name already exist'
          })
        }
        setLoading(false)
      }
    }
  ])

  const handleBlur = e => {
    if (e.target.value && !isEdit) {
      const tenantKey = getValues('tenantKey')
      isNameInUse.trigger({ tenantKey })
      setLoading(true)
      envInfo &&
        setValue(
          'tenantUrl',
          buildToolkitUrl(tenantKey, envInfo.stage, envInfo.tld)
        )
    }
  }

  const onNext = async () => {
    const result = await trigger(['tenantKey', 'name', 'clientName', 'region', 'toolkitType'])
    result && handleNextClick()
  }

  // For non admin mode we want to hide the description of non editable fields and make them full width
  const defaultFormInputRowViewOnlyProps = isAdminMode ? {} : {
    fillWidth: true,
    description: null,
    direction: 'column',
    DescriptionComponent: (<></>)
  }

  return (
    <div className={classes.root}>
      <Grid container>
        <Grid container alignItems='center' className={mainClasses.ctnTitle}>
          <Grid item xs={8}>
            <Typography variant='h6'>Details</Typography>
          </Grid>
          <Grid container item justifyContent='flex-end' xs={4}>
            {!isEdit && (
              <ActionButton
                variant='primary'
                dataTest='create-tenant-save-button'
                onClick={onNext}
                // disabled={nextBtnDisabled}
                className={mainClasses.navBtn}
              >Next
              </ActionButton>
            )}
            {isEdit && isAdminMode && (
              <ActionButton
                variant='primary'
                dataTest='button-save-tenant-sso'
                onClick={onUpdateTenant}
              >Save Changes
              </ActionButton>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} className={!isAdminMode ? classes.greyBackground : ''}>
          <FormInputRow
            title='Client Name'
            description='This information is mandatory, but it does not need to be unique.'
            autofocus
            smallFormTitles
            {...defaultFormInputRowViewOnlyProps}
            InputComponent={
              <Input
                label='Client Name'
                name='clientName'
                control={form.control}
                disabled={!isAdminMode}
                rules={{ required: true }}
                valid={errors.clientName === undefined}
                error='Client Name is required'
                showErrors
              />
            }
          />
        </Grid>
        <Grid item xs={12} className={!isAdminMode ? classes.greyBackground : ''}>
          <FormInputRow
            title='Toolkit Name'
            description='The toolkit name must be unique and cannot be changed after the creation of the toolkit, even when new products are added or removed. Therefore, it is encouraged to choose a non-product-specific name.'
            smallFormTitles
            {...defaultFormInputRowViewOnlyProps}
            InputComponent={
              <Input
                rules={{
                  required: {
                    value: 'true',
                    message: 'Toolkit Name is required'
                  }
                }}
                onBlur={handleBlur}
                disabled={!isAdminMode}
                label='Name'
                name='name'
                control={form.control}
                valid={errors.name === undefined}
                error={errors.name?.message}
                showErrors
              />
            }
          />
        </Grid>

        <Grid item xs={12} className={!isAdminMode ? classes.greyBackground : ''}>
          <FormInputRow
            title='Toolkit Type'
            description='The toolkit type, small business toolkits have limited features and max number of users.'
            autofocus
            smallFormTitles
            {...defaultFormInputRowViewOnlyProps}
            InputComponent={
              <Controller
                name='toolkitType'
                control={form.control}
                rules={{ required: true }}
                render={({ field }) =>
                  <FormControl error={errors.toolkitType !== undefined} variant='outlined' sx={{ width: '100%' }}>
                    <InputLabel id='toolkitType-select-lbl'>Toolkit Type</InputLabel>
                    <Select
                      variant='outlined'
                      {...field}
                      disabled={!isAdminMode}
                      id='toolkitType-select'
                      labelId='toolkitType-select-lbl'
                      value={field.value || TOOLKIT_TYPE.enterprise}
                    >
                      {toolkitTypeOptions.map(opt => <MenuItem value={opt.value} key={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                    {errors.toolkitType !== undefined && (
                      <FormHelperText>Toolkit Type is required</FormHelperText>
                    )}
                  </FormControl>}
              />
            }
          />
        </Grid>

        <Grid item xs={12} className={isEdit ? classes.greyBackground : ''}>
          <FormInputRow
            title='Toolkit ID'
            description="This information is automatically configured after entering the toolkit name. It will be used as the subdomain of the toolkit's URL."
            smallFormTitles
            {...defaultFormInputRowViewOnlyProps}
            InputComponent={
              <Input
                label='Toolkit ID'
                name='tenantKey'
                control={form.control}
                valid={errors.tenantKey === undefined}
                disabled={!isAdminMode}
                error={errors.tenantKey?.message}
                rules={{
                  required: {
                    value: 'true',
                    message: 'Toolkit ID is required'
                  }
                }}
                helperText={loading && 'Checking if this ID is available...'}
                className={
                  errors.tenantKey === undefined ? classes.loading : ''
                }
                onBlur={handleBlur}
                InputProps={isEdit ? { readOnly: true } : ''}
                showErrors
              />
            }
          />
        </Grid>
        <Grid item xs={12} className={classes.greyBackground}>
          <FormInputRow
            title='Toolkit URL'
            description='The web address for the Toolkit.'
            smallFormTitles
            {...defaultFormInputRowViewOnlyProps}
            InputComponent={
              <Input
                label='Toolkit URL'
                name='tenantUrl'
                control={form.control}
                InputProps={{
                  readOnly: true
                }}
                disabled={!isAdminMode}
                className={getValues('tenantUrl') ? '' : classes.disableTouch}
              />
            }
          />
        </Grid>
        <Grid item xs={12} className={!isAdminMode ? classes.greyBackground : ''}>
          <FormInputRow
            title='Estimated Number of Users'
            description='This is the maximum number of users that the client is paying to have on their toolkit. This information will feed the reporting tool and clients will not be cut off if their users exceed the number you specify.'
            smallFormTitles
            {...defaultFormInputRowViewOnlyProps}
            InputComponent={
              <Input
                label='Estimated Number of Users'
                name='usersLimit'
                InputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                textRegex='^[0-9\b]+$'
                disabled={!isAdminMode}
                control={form.control}
              />
            }
          />
        </Grid>
        {isSBT && (
          <Grid item xs={12} className={!isAdminMode ? classes.greyBackground : ''}>
            <FormInputRow
              title='Max Number of Users'
              description='Use this to configure the maximum number of users in a SBT Toolkit. This field has no impact on Enterprise Toolkits.'
              smallFormTitles
              {...defaultFormInputRowViewOnlyProps}
              InputComponent={
                <Input
                  label='Max Number of Users'
                  name='maxNumberOfUsers'
                  textRegex='^[0-9\b]+$'
                  disabled={!isAdminMode}
                  control={form.control}
                />
              }
            />
          </Grid>
        )}

        <Grid item xs={12} className={!isAdminMode || isEdit ? classes.greyBackground : ''}>
          <FormInputRow
            title='Toolkit Region'
            description='The closest region to the client. This will be used to determine the region the tenant application will be deployed to.'
            autofocus
            smallFormTitles
            {...defaultFormInputRowViewOnlyProps}
            InputComponent={
              <Controller
                name='region'
                control={form.control}
                rules={{ required: false }}
                render={({ field }) =>
                  <FormControl error={errors.region !== undefined} variant='outlined' sx={{ width: '100%' }}>
                    <InputLabel id='toolkitRegion-select-lbl'>Toolkit Region</InputLabel>
                    <Select
                      variant='outlined'
                      {...field}
                      disabled={!isAdminMode || isEdit}
                      id='toolkitRegion-select'
                      labelId='toolkitRegion-select-lbl'
                      value={field.value && field.value.length > 0 ? field.value : AWS_REGIONS.london}
                    >
                      {toolkitRegionOptions.map(opt => <MenuItem value={opt.value} key={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                    {errors.region !== undefined && (
                      <FormHelperText>Toolkit Region is required</FormHelperText>
                    )}
                  </FormControl>}
              />
            }
          />
        </Grid>
        {isAdminMode && showUsernameLogin && (
          <>
            <Divider className={classes.detailsDivider} />
            <UsernameLogin />
          </>
        )}
        {isAdminMode && isClub && (
          <>
            <Divider className={classes.detailsDivider} />
            <StubbedContent />
            <Divider className={classes.detailsDivider} />
            <Metering />
          </>
        )}
        {isEdit && isAdminMode && <TempUserField />}
      </Grid>
    </div>
  )
}

export default DetailTab
